/* eslint-disable react/require-default-props -- React deprecó defaultProps en componentes
   de función (avisa por consola en cada render): los valores por defecto van en la firma. */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  Add,
  Close,
  DeleteOutline,
  Edit,
  LibraryMusic,
  MusicNote,
  PlayArrow,
  QueueMusic,
  Schedule,
  Spa
} from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import PlayAlbumBuilder from 'components/play/music/PlayAlbumBuilder';
import PlayTrackList from 'components/play/music/PlayTrackList';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  DELETE_PLAY_MUSIC_ALBUM,
  PLAY_MUSIC_ALBUMS,
  PLAY_MUSIC_TRACKS
} from 'api/graphql/playMusic';
import { formatDuration } from 'components/play/workoutEstimate';

/**
 * OTRIX Play — detalle de una práctica de recuperación ANTES de ejecutarla. Aquí muere el
 * auto-start: la card abre esto y el cronómetro solo arranca con el botón Iniciar.
 * Incluye la secuencia, recomendaciones de ambiente y el selector de música de la librería.
 *
 * La música se elige de tres formas: una pista suelta, la categoría completa o una playlist
 * (propia o curada por OTRIX), que además se puede armar sin salir de aquí.
 */
function PlayRecoveryDetailModal({ practice = null, open, onClose, onStart }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  // null = sin tocar (usa la pista por defecto de la práctica); '' = "Sin música" explícito.
  const [trackId, setTrackId] = useState(null);
  // single = una pista | album = categoría completa | playlist = álbum guardado.
  const [mode, setMode] = useState('single');
  const [category, setCategory] = useState('');
  const [albumId, setAlbumId] = useState('');
  const [builderAlbum, setBuilderAlbum] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (open) {
      setTrackId(null);
      // La práctica puede traer su propio álbum de arranque; si lo trae, manda sobre la pista.
      setMode(practice?.musicAlbum ? 'playlist' : 'single');
      setCategory('');
      setAlbumId(practice?.musicAlbum?.id ? String(practice.musicAlbum.id) : '');
    }
  }, [open, practice?.key, practice?.id, practice?.musicAlbum]);

  const { data: musicData } = useQuery(PLAY_MUSIC_TRACKS, {
    variables: {},
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  const { data: albumsData } = useQuery(PLAY_MUSIC_ALBUMS, {
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  const [deleteAlbum] = useMutation(DELETE_PLAY_MUSIC_ALBUM, {
    refetchQueries: [{ query: PLAY_MUSIC_ALBUMS }]
  });

  const handleDeleteAlbum = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await deleteAlbum({ variables: { id: target.id } });
      setAlbumId('');
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: `album-del-${Date.now()}`,
            variant: 'success',
            message: t('albums.deleted', { ns: 'play', defaultValue: 'Playlist eliminada.' })
          })
        )
      );
    } catch {
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: `album-del-err-${Date.now()}`,
            variant: 'error',
            message: t('albums.deleteError', {
              ns: 'play',
              defaultValue: 'No pudimos eliminar la playlist.'
            })
          })
        )
      );
    }
  };

  if (!practice) return null;

  const tracks = musicData?.playMusicTracks || [];
  const defaultTrackId = practice.musicTrack?.id || '';
  const effectiveTrackId = trackId === null ? defaultTrackId : trackId;
  const selectedTrack = effectiveTrackId
    ? tracks.find((tr) => String(tr.id) === String(effectiveTrackId)) || practice.musicTrack || null
    : null;

  // Categorías que REALMENTE tienen pistas: nada de ofrecer álbumes vacíos.
  const categories = [...new Set(tracks.map((tr) => tr.category).filter(Boolean))];
  // El backend ya devuelve las pistas ordenadas por `position`, así que filtrar en cliente
  // conserva el orden del álbum y evita una segunda consulta al cambiar de categoría.
  const categoryTracks = category ? tracks.filter((tr) => tr.category === category) : [];
  const isAlbum = mode === 'album';
  const isPlaylist = mode === 'playlist';

  const albums = albumsData?.playMusicAlbums || [];
  // Si la práctica trae un álbum que ya no está en la lista (oculto), igual se puede sonar:
  // el objeto viene con sus pistas dentro de la propia práctica.
  const selectedAlbum = albumId
    ? albums.find((al) => String(al.id) === String(albumId)) ||
      (String(practice.musicAlbum?.id) === String(albumId) ? practice.musicAlbum : null)
    : null;
  const playlistTracks = selectedAlbum?.tracks || [];

  let startTracks = [selectedTrack].filter(Boolean);
  if (isAlbum) startTracks = categoryTracks;
  if (isPlaylist) startTracks = playlistTracks;

  const albumEmpty = isAlbum && category && categoryTracks.length === 0;

  const exerciseSteps = practice.steps.filter((s) => s.type !== 'rest');

  const tips = [
    t('recovery.detail.tipSpace', {
      ns: 'play',
      defaultValue: 'Busca un espacio tranquilo y sin interrupciones.'
    }),
    t('recovery.detail.tipClothes', {
      ns: 'play',
      defaultValue: 'Ropa cómoda y, si puedes, una superficie suave.'
    }),
    t('recovery.detail.tipMusic', {
      ns: 'play',
      defaultValue: 'La música suave ayuda a mantener el ritmo.'
    })
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'surface.content',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'hairline.main'
          }
        }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>
              {practice.title}
            </Typography>
            <Stack
              direction="row"
              spacing={0.4}
              alignItems="center"
              sx={{ color: 'text.secondary', flex: 'none' }}>
              <Schedule sx={{ fontSize: 14 }} />
              <Typography
                variant="caption"
                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                {formatDuration(practice.totalSeconds)}
              </Typography>
            </Stack>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
            aria-label={t('recovery.close', { ns: 'play' })}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <DialogContent>
          <Stack spacing={2.5}>
            {practice.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {practice.description}
              </Typography>
            )}

            {/* Secuencia (solo prácticas guiadas) */}
            {exerciseSteps.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                  {t('recovery.detail.sequence', { ns: 'play', defaultValue: 'Secuencia' })}
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                  {exerciseSteps.map((step, i) => (
                    <Stack
                      key={step.id || step.key || i}
                      direction="row"
                      spacing={1.25}
                      alignItems="center">
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          flex: 'none',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          color: 'gold.main',
                          border: '1px solid',
                          borderColor: 'hairline.main',
                          backgroundColor: 'surface.overlay'
                        }}>
                        {i + 1}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {step.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontVariantNumeric: 'tabular-nums',
                          flex: 'none'
                        }}>
                        {formatDuration(step.duration_seconds)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Recomendaciones de ambiente */}
            <Box
              sx={{
                p: 1.75,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'surface.overlay'
              }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Spa sx={{ fontSize: 16, color: 'gold.main' }} />
                <Typography variant="caption" fontWeight={800}>
                  {t('recovery.detail.tipsTitle', {
                    ns: 'play',
                    defaultValue: 'Prepara el momento'
                  })}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {tips.map((tip) => (
                  <Typography key={tip} variant="caption" sx={{ color: 'text.secondary' }}>
                    · {tip}
                  </Typography>
                ))}
              </Stack>
            </Box>

            {/* Música: una pista suelta, la categoría entera o una playlist guardada */}
            <Stack spacing={1.25}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_, v) => {
                  if (!v) return;
                  setMode(v);
                  // Al pasar a álbum arrancamos por la categoría de la pista que ya venía elegida.
                  if (v === 'album' && !category) setCategory(selectedTrack?.category || '');
                }}
                aria-label={t('recovery.detail.musicMode', {
                  ns: 'play',
                  defaultValue: 'Modo de música'
                })}
                sx={{
                  alignSelf: 'flex-start',
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    borderColor: 'hairline.main',
                    transition:
                      'color 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms cubic-bezier(0.32,0.72,0,1)',
                    '&:hover': { color: 'text.primary', backgroundColor: 'surface.overlay' },
                    '&:active': { transform: 'scale(0.98)' },
                    '&.Mui-selected': {
                      color: 'gold.main',
                      backgroundColor: 'gold.soft',
                      '&:hover': { backgroundColor: 'gold.soft' }
                    }
                  }
                }}>
                <ToggleButton
                  value="single"
                  aria-label={t('recovery.detail.modeSingle', {
                    ns: 'play',
                    defaultValue: 'Pista suelta'
                  })}>
                  <MusicNote sx={{ fontSize: 15, mr: 0.5 }} />
                  {t('recovery.detail.modeSingle', { ns: 'play', defaultValue: 'Pista suelta' })}
                </ToggleButton>
                <ToggleButton
                  value="album"
                  aria-label={t('recovery.detail.modeAlbum', {
                    ns: 'play',
                    defaultValue: 'Categoría completa'
                  })}>
                  <QueueMusic sx={{ fontSize: 16, mr: 0.5 }} />
                  {t('recovery.detail.modeAlbum', {
                    ns: 'play',
                    defaultValue: 'Categoría completa'
                  })}
                </ToggleButton>
                <ToggleButton
                  value="playlist"
                  aria-label={t('recovery.detail.modePlaylist', {
                    ns: 'play',
                    defaultValue: 'Mis playlists'
                  })}>
                  <LibraryMusic sx={{ fontSize: 16, mr: 0.5 }} />
                  {t('recovery.detail.modePlaylist', { ns: 'play', defaultValue: 'Mis playlists' })}
                </ToggleButton>
              </ToggleButtonGroup>

              {mode === 'single' && (
                <TextField
                  select
                  size="small"
                  label={t('recovery.detail.music', {
                    ns: 'play',
                    defaultValue: 'Música relajante'
                  })}
                  value={effectiveTrackId ? String(effectiveTrackId) : ''}
                  onChange={(e) => setTrackId(e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <MusicNote sx={{ fontSize: 16, color: 'gold.main', mr: 1 }} />
                  }}>
                  <MenuItem value="">
                    {t('recovery.detail.noMusic', { ns: 'play', defaultValue: 'Sin música' })}
                  </MenuItem>
                  {tracks.map((track) => (
                    <MenuItem key={track.id} value={String(track.id)}>
                      {track.title}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {isAlbum && (
                <TextField
                  select
                  size="small"
                  label={t('recovery.detail.musicCategory', {
                    ns: 'play',
                    defaultValue: 'Categoría'
                  })}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <QueueMusic sx={{ fontSize: 17, color: 'gold.main', mr: 1 }} />
                  }}
                  helperText={
                    albumEmpty
                      ? t('recovery.detail.albumEmpty', {
                          ns: 'play',
                          defaultValue: 'Esta categoría aún no tiene pistas.'
                        })
                      : ''
                  }>
                  <MenuItem value="">
                    {t('recovery.detail.noMusic', { ns: 'play', defaultValue: 'Sin música' })}
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {t(`musicAdmin.categories.${cat}`, { ns: 'play', defaultValue: cat })}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {isAlbum && categoryTracks.length > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('recovery.detail.albumCount', {
                    ns: 'play',
                    count: categoryTracks.length,
                    defaultValue: '{{count}} pistas, se reproducen en orden'
                  })}
                </Typography>
              )}

              {isPlaylist && (
                <>
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <TextField
                      select
                      size="small"
                      label={t('recovery.detail.playlist', {
                        ns: 'play',
                        defaultValue: 'Playlist'
                      })}
                      value={albumId}
                      onChange={(e) => setAlbumId(e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <LibraryMusic sx={{ fontSize: 17, color: 'gold.main', mr: 1 }} />
                        )
                      }}
                      sx={{ flex: 1, minWidth: 0 }}>
                      <MenuItem value="">
                        {t('recovery.detail.noMusic', { ns: 'play', defaultValue: 'Sin música' })}
                      </MenuItem>
                      {albums.map((al) => (
                        <MenuItem key={al.id} value={String(al.id)}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                              {al.title}
                            </Typography>
                            {!al.is_personal && (
                              <Chip
                                size="small"
                                label={t('albums.curated', { ns: 'play', defaultValue: 'OTRIX' })}
                                sx={{
                                  bgcolor: 'gold.soft',
                                  color: 'gold.main',
                                  fontWeight: 700,
                                  height: 18,
                                  fontSize: '0.62rem'
                                }}
                              />
                            )}
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Editar y borrar solo tienen sentido en las playlists propias. */}
                    {selectedAlbum?.is_personal && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setBuilderAlbum(selectedAlbum);
                            setBuilderOpen(true);
                          }}
                          aria-label={t('albums.edit', {
                            ns: 'play',
                            defaultValue: 'Editar playlist'
                          })}
                          sx={{ mt: 0.5, color: 'text.secondary' }}>
                          <Edit sx={{ fontSize: 17 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(selectedAlbum)}
                          aria-label={t('albums.delete', {
                            ns: 'play',
                            defaultValue: 'Eliminar playlist'
                          })}
                          sx={{ mt: 0.5, color: 'sessionStatus.absent.fg' }}>
                          <DeleteOutline sx={{ fontSize: 17 }} />
                        </IconButton>
                      </>
                    )}
                  </Stack>

                  <Button
                    size="small"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      setBuilderAlbum(null);
                      setBuilderOpen(true);
                    }}
                    sx={{
                      alignSelf: 'flex-start',
                      px: 0.75,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: 'gold.main',
                      transition: 'opacity 200ms cubic-bezier(0.32,0.72,0,1)',
                      '&:hover': { backgroundColor: 'transparent', opacity: 0.85 },
                      '&:active': { transform: 'scale(0.98)' }
                    }}>
                    {t('albums.create', { ns: 'play', defaultValue: 'Crear playlist' })}
                  </Button>

                  {albums.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('albums.noneYet', {
                        ns: 'play',
                        defaultValue:
                          'Todavía no tienes playlists. Arma la primera con tus pistas favoritas.'
                      })}
                    </Typography>
                  )}
                </>
              )}

              {/* Vista previa de la cola: se ve exactamente lo que va a sonar y en qué orden. */}
              {startTracks.length > 1 && (
                <Box
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.overlay',
                    px: 0.5,
                    py: 0.5
                  }}>
                  <PlayTrackList
                    tracks={startTracks}
                    currentIndex={-1}
                    readOnly
                    maxVisible={5}
                    ariaLabel={t('recovery.detail.queuePreview', {
                      ns: 'play',
                      defaultValue: 'Pistas que sonarán'
                    })}
                  />
                </Box>
              )}
            </Stack>

            <Button
              fullWidth
              startIcon={<PlayArrow />}
              onClick={() => onStart(startTracks)}
              sx={{ ...premiumButtonSx, py: 1.2 }}>
              {t('recovery.start', { ns: 'play' })}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <PlayAlbumBuilder
        open={builderOpen}
        album={builderAlbum}
        personal
        onClose={() => setBuilderOpen(false)}
        onSaved={(saved) => {
          // Recién guardada queda seleccionada: el usuario la armó para usarla ahora.
          if (saved?.id) setAlbumId(String(saved.id));
        }}
      />

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteAlbum}
        title={t('albums.deleteTitle', { ns: 'play', defaultValue: 'Eliminar playlist' })}
        message={t('albums.deleteMessage', {
          ns: 'play',
          name: deleteTarget?.title || '',
          defaultValue:
            '¿Eliminar "{{name}}"? Las pistas siguen en la librería, solo se borra la lista.'
        })}
      />
    </>
  );
}

PlayRecoveryDetailModal.propTypes = {
  practice: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired
};

export default PlayRecoveryDetailModal;
